import CloseIcon from '@mui/icons-material/Close'
import { useEffect, type FormEvent } from 'react'
import { ModalOverlay } from '../ModalOverlay'
import LabOrderForm, { type ClinicDetails } from './LabOrderForm'
import {
  computeOrderTotals,
  type LabOrderFormValues,
  type LabPriceListItem,
} from './labOrderFormUtils'

const FORM_ID = 'send-lab-order-modal-form'

type SendLabOrderModalProps = {
  open: boolean
  onClose: () => void
  partners: {
    id: string
    labId: string
    name: string
    address?: string
    phone?: string
  }[]
  values: LabOrderFormValues
  onChange: (values: LabOrderFormValues) => void
  onSubmit: (e: FormEvent) => void
  submitting: boolean
  error?: string
  clinicDetails?: ClinicDetails
  priceListItems?: LabPriceListItem[]
  priceListLoading?: boolean
  patientError?: boolean
}

function formatLkr(amount: number) {
  return `LKR ${(Number(amount) || 0).toLocaleString('en-LK')}`
}

export default function SendLabOrderModal({
  open,
  onClose,
  partners,
  values,
  onChange,
  onSubmit,
  submitting,
  error,
  clinicDetails,
  priceListItems,
  priceListLoading,
  patientError,
}: SendLabOrderModalProps) {
  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose()
    }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose, submitting])

  if (!open) return null

  const itemCount = values.invoiceItems.filter((row) => row.item.trim()).length
  const totals = computeOrderTotals(values)
  const selectedLab = partners.find((p) => p.id === values.labPartnerId)
  const canSend =
    !submitting &&
    Boolean(values.labPartnerId) &&
    Boolean(values.caseTitle.trim()) &&
    values.toothNos.length > 0 &&
    itemCount > 0

  return (
    <ModalOverlay
      className="fixed inset-0 z-[200] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-lab-order-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose()
      }}
    >
      <div
        className="relative flex h-[min(96dvh,920px)] w-full max-w-3xl flex-col rounded-t-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800 sm:h-auto sm:max-h-[min(90dvh,880px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3.5 dark:border-slate-700 sm:px-6">
          <div className="min-w-0">
            <h2
              id="send-lab-order-title"
              className="text-base font-semibold text-slate-900 dark:text-slate-100"
            >
              Send to lab
            </h2>
            <p className="mt-0.5 text-xs leading-relaxed text-slate-500">
              {selectedLab
                ? `Sending to ${selectedLab.name}`
                : 'Choose a lab, confirm the case, add items, then send.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex size-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:hover:bg-slate-700"
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          {error ? (
            <p
              role="alert"
              className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
            >
              {error}
            </p>
          ) : null}

          {partners.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center dark:border-slate-600 dark:bg-slate-800/50">
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                No partner labs available
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Add an active lab partner before sending an order.
              </p>
            </div>
          ) : (
            <LabOrderForm
              formId={FORM_ID}
              variant="modal"
              hideActions
              patientReadOnly
              partners={partners}
              values={values}
              onChange={onChange}
              onSubmit={onSubmit}
              submitting={submitting}
              submitLabel="Send to lab"
              clinicDetails={clinicDetails}
              priceListItems={priceListItems}
              priceListLoading={priceListLoading}
              patientError={patientError}
            />
          )}
        </div>

        {partners.length > 0 ? (
          <footer className="flex shrink-0 flex-col gap-3 border-t border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-800 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <p className="text-xs text-slate-500 sm:text-sm">
              {itemCount > 0 ? (
                <>
                  <span className="font-medium text-slate-700 dark:text-slate-200">
                    {itemCount} item{itemCount === 1 ? '' : 's'}
                  </span>
                  <span className="mx-1.5 text-slate-300 dark:text-slate-600">
                    ·
                  </span>
                  <span className="font-semibold text-primary">
                    {formatLkr(totals.finalTotal)}
                  </span>
                </>
              ) : (
                'Add at least one lab item to enable Send'
              )}
            </p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700/50"
              >
                Cancel
              </button>
              <button
                type="submit"
                form={FORM_ID}
                disabled={!canSend}
                className="rounded-lg bg-gradient-to-r from-primary to-primaryLight px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[9.5rem]"
              >
                {submitting ? 'Sending…' : 'Send to lab'}
              </button>
            </div>
          </footer>
        ) : (
          <footer className="flex shrink-0 justify-end border-t border-slate-200 px-4 py-3 dark:border-slate-700 sm:px-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700/50"
            >
              Close
            </button>
          </footer>
        )}
      </div>
    </ModalOverlay>
  )
}
